import React, {useContext, useEffect, useState, useRef} from 'react';
import axios from "@common/axios";
import ClassCard from "./components/ClassCard";
import CardButton from "../reports/CardButton";
import {AddCircleRounded, GridViewRounded, ListRounded, SchoolRounded, UploadFileRounded} from "@mui/icons-material";
import CircularLoader from "@common/CircularLoader";
import {Select, Form, Input, Pagination, Radio, Popover} from 'antd';
import TableFilterControls from "@common/TableFilterControls";
import TableFilterControlsForm from "@common/TableFilterControlsForm";
import TableFilterControlsSection from "@common/TableFilterControlsSection";
import Button from "@common/Button";
import ClassesTable from "./components/ClassesTable";
import {NavigationContext} from "@common/context/NavigationContext";
import {useLocation, useSearchParams} from "wouter";
import Tooltip from "@common/Tooltip";
import ExportToCsvButton from "@common/ExportToCsvButton";
import StartBulkClassUpdateDialog from "../schoolClasses/BulkClassUpdate/StartBulkClassUpdateDialog";
import {UserContext} from "@context/UserContext";
import PageHeader from "@common/PageHeader";

const { Option } = Select;

const VIEW_MODE_STORAGE_KEY = 'schoolClasses_viewMode';

const componentStateCache = {
    view: 'teacher',
    currentPage: 1,
};

const getViewModeFromStorage = () => {
    try {
        const savedViewMode = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
        return savedViewMode === 'list' ? 'list' : 'grid';
    } catch (error) {
        console.warn('Unable to access localStorage:', error);
        return 'grid';
    }
};

const saveViewModeToStorage = (viewMode) => {
    try {
        localStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode);
    } catch (error) {
        console.warn('Unable to save to localStorage:', error);
    }
};

export const SchoolClasses = () => {
    const { user } = useContext(UserContext);
    const [teacherClasses, setTeacherClasses] = useState([]);
    const [allClasses, setAllClasses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState(componentStateCache.view);
    const [viewMode, setViewMode] = useState(() => getViewModeFromStorage());
    const [currentPage, setCurrentPage] = useState(componentStateCache.currentPage);
    const [pageSize] = useState(8);
    const [searchQuery, setSearchQuery] = useState('');
    const [isClassCardRefreshed, setIsClassCardRefreshed] = useState(false);
    const [ isBulkClassUpdateDialogOpen, setIsBulkClassUpdateDialogOpen ] = useState(false);
    const [refetchTrigger, setRefetchTrigger] = useState(0);
    const { isDrawerVisible, setDrawerComponentName, setDrawerVisible, setDrawerLocation } = useContext(NavigationContext);
    const [searchParams, setSearchParams] = useSearchParams();
    const [, navigate] = useLocation();
    const isInitialMount = useRef(true);

    useEffect(() => {
        const fetchClasses = async () => {
            try {
                const [teacherResponse, allClassesResponse] = await Promise.all([
                    axios.get('/web/ui/myClasses/viewMyClasses'),
                    axios.get('/web/ui/myClasses/viewSchoolClasses')
                ]);

                setTeacherClasses(teacherResponse.data);
                setAllClasses(allClassesResponse.data);
            } catch (error) {
                console.error("Error fetching class data:", error);
            } finally {
                setLoading(false);
            }
        };

        if (!isDrawerVisible || isClassCardRefreshed) {
            fetchClasses();
            setRefetchTrigger(prev => prev + 1);

            if (isClassCardRefreshed) {
                setIsClassCardRefreshed(false);
            }
        }

    }, [isDrawerVisible, isClassCardRefreshed]);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
        } else {
            setCurrentPage(1);
        }
    }, [view, viewMode, searchQuery]);

    useEffect(() => {
        componentStateCache.view = view;
    }, [view]);

    useEffect(() => {
        componentStateCache.currentPage = currentPage;
    }, [currentPage]);

    useEffect(() => {
        saveViewModeToStorage(viewMode);
    }, [viewMode]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    }

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    }

    const handleViewModeChange = (e) => {
        const newViewMode = e.target.value;
        setViewMode(newViewMode);
    };

    const getFilteredClasses = () => {
        const baseClasses = view === 'teacher' ? teacherClasses : allClasses;

        if (!searchQuery.trim()) {
            return baseClasses;
        }

        return baseClasses.filter(classItem =>
            classItem.className?.toLowerCase().includes(searchQuery.toLowerCase())
        );
    };

    const displayedClasses = getFilteredClasses();

    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedClasses = displayedClasses.slice(startIndex, endIndex);

    const handleOpenDrawer = (classId, componentName) => {
        if(classId){
            setSearchParams({
                editClassId: classId
            })
        }
        if(!classId){
            setSearchParams({
                editClassId: undefined
            })
        }
        setDrawerComponentName(componentName);
        setDrawerLocation('right');
        setDrawerVisible(true);
    }

    const exportColumns = [
        {
            title: 'Class Name',
            dataIndex: 'className',
            key: 'className',
        },
        {
            title: 'Class Key',
            dataIndex: 'classKey',
            key: 'classKey',
        },
        {
            title: 'Teacher Count',
            dataIndex: 'teacherCount',
            key: 'teacherCount',
        },
        {
            title: 'Student Count',
            dataIndex: 'studentCount',
            key: 'studentCount',
        },
        {
            title: 'Year Level',
            dataIndex: 'yearLevel',
            key: 'yearLevel',
        },
        {
            title: 'Calendar Year',
            dataIndex: 'calendarYear',
            key: 'calendarYear',
        }
    ];

    const exportData = displayedClasses.map(classItem => ({
        className: classItem.className || 'N/A',
        classKey: classItem.classCode,
        studentCount: classItem.totalStudents || 0,
        teacherCount: classItem.totalTeachers || 0,
        yearLevel: classItem.schoolYearDescription || 'N/A',
        calendarYear: classItem.calendarYear || 'N/A'
    }));

    if (loading) {
        return <CircularLoader />
    }

    const viewOptions = [
        {
            label: <><Tooltip title={'Grid view'}><span data-gtm-value="classes-view-grid"><GridViewRounded
                style={{marginTop: 3}}/></span></Tooltip><span className={'ch-sr-only'}>Grid view</span></>,
            value: 'grid'
        },
        {
            label: <><Tooltip title={'List view'}><span data-gtm-value="classes-view-list"><ListRounded
                style={{marginTop: 3}}/></span></Tooltip><span className={'ch-sr-only'}>List view</span></>,
            value: 'list'
        }
    ];

    const getPagination = () => {
        if(displayedClasses.length === 0) return;
        return(
            <div className={'ch-classes-pagination-wrapper'}>
                <Pagination
                    current={currentPage}
                    total={displayedClasses.length}
                    pageSize={pageSize}
                    onChange={handlePageChange}
                    showSizeChanger={false}
                    showQuickJumper={false}
                    showTotal={(total, range) =>
                        `${range[0]}-${range[1]} of ${total} classes`
                    }
                    className={'ch-classes-pagination'}
                />
            </div>
        )
    }

    const getNoResultText = () => {
        if(view === 'teacher'){
            return (
                <div className={'ch-my-classes-no-results'}>
                    <p className={'ch-warning-text'} style={{width: 'fit-content'}}>
                        No classes found matching "{searchQuery}". Try searching
                    </p>
                    <Button type={'text_underline'} onClick={() => setView('all')}>All Classes</Button>
                </div>
            )
        }
        return (
            <p className={'ch-warning-text'} style={{width: 'fit-content'}}>
                No classes found matching "{searchQuery}" in All Classes.
            </p>
        )
    }

    const handleBulkClassUpdateClick = () => {
        if(user?.brandCodes.length > 1){
            setIsBulkClassUpdateDialogOpen(true);
        } else {
            navigate(`/web/ui/view/classes/schoolClasses/bulkClassUpdate`);
        }
    }

    return (
        <div>
            <PageHeader
                title={'Classes'}
                buttons={
                    <>
                        <ExportToCsvButton
                            data={exportData}
                            columns={exportColumns}
                            fileName="school_classes.csv"
                            disabled={displayedClasses.length === 0}
                            buttonText={`Export Classes List`}
                        />
                        <Button
                            type={'outline'}
                            onClick={() => handleOpenDrawer(null, 'AddClassDrawer')}
                            icon={<SchoolRounded />}
                        >
                            Add Class
                        </Button>
                        <Button
                            onClick={() => handleBulkClassUpdateClick()}
                            icon={<UploadFileRounded />}
                        >
                            Bulk Class Update
                        </Button>
                    </>
                }
            />
            <div>
                <TableFilterControls
                    filterContent={
                        <TableFilterControlsForm
                            customButton={
                                <Form.Item>
                                    <Radio.Group
                                        block
                                        options={viewOptions}
                                        value={viewMode}
                                        onChange={handleViewModeChange}
                                        optionType="button"
                                        buttonStyle="solid"
                                    />
                                </Form.Item>
                            }
                        >
                            <TableFilterControlsSection showDivider={false}>
                                <Form.Item label={<span>View</span>} htmlFor="view-select">
                                    <Select
                                        id="view-select"
                                        value={view}
                                        onChange={value => setView(value)}
                                    >
                                        <Option value="teacher">My Classes</Option>
                                        <Option value="all">All Classes</Option>
                                    </Select>
                                </Form.Item>
                                <Form.Item label={<span>Search for a class</span>} htmlFor="class-search-input">
                                    <Input
                                        id="class-search-input"
                                        placeholder="Search by class name..."
                                        value={searchQuery}
                                        onChange={handleSearchChange}
                                        allowClear
                                    />
                                </Form.Item>
                            </TableFilterControlsSection>
                        </TableFilterControlsForm>
                    }
                />
            </div>

            <div className={'ch-my-classes'}>
                {viewMode === 'grid' ? (
                    <>
                        {getPagination()}
                        <div className={'ch-classes-grid'}>
                            {paginatedClasses.map(teacherClass => {
                                const isUsersClass = teacherClasses.some(tc => tc.classId === teacherClass.classId);

                                return (
                                    <ClassCard
                                        classDetails={teacherClass}
                                        key={teacherClass.classId}
                                        handleEditClass={() => handleOpenDrawer(teacherClass.classId, 'EditClassDrawer')}
                                        handleAddStudent={() => handleOpenDrawer(teacherClass.classId, 'ManageClassUsers')}
                                        handleRefreshClasses={() => setIsClassCardRefreshed(true)}
                                        addUserTooltipText={'Update Users'}
                                        usersClass={isUsersClass}
                                        useDemoLinkButton={false}
                                    />
                                );
                            })}
                        </div>

                        {view === 'teacher' && teacherClasses.length === 0 && !searchQuery && (
                            <div>
                                <p className={'ch-warning-text'} style={{ width: 'fit-content' }}>
                                    You're not a teacher of any classes.
                                </p>
                                <CardButton
                                    cardTitle={'Add yourself to a class'}
                                    cardIcon={<AddCircleRounded fontSize={'large'} />}
                                    onClickFn={() => setView('all')}
                                />
                            </div>
                        )}

                        {searchQuery && displayedClasses.length === 0 && (
                            <div>
                                {getNoResultText()}
                            </div>
                        )}

                        {getPagination()}
                    </>
                ) : (
                    <ClassesTable
                        view={view}
                        searchQuery={searchQuery}
                        teacherClasses={teacherClasses}
                        handleOpenDrawer={handleOpenDrawer}
                        handleRefreshClasses={() => {
                            setIsClassCardRefreshed(true);
                            setRefetchTrigger(prev => prev + 1);
                        }}
                        addUserTooltipText={'Update Users'}
                        refetchTrigger={refetchTrigger}
                    />
                )}
            </div>
            <StartBulkClassUpdateDialog
                open={isBulkClassUpdateDialogOpen}
                setClose={() => setIsBulkClassUpdateDialogOpen(false)}
                callback={() => {
                    navigate('/web/ui/view/classes/schoolClasses/bulkClassUpdate')
                }}
            />
        </div>
    );
};

export default SchoolClasses;